import React, { useState, useEffect } from 'react'
import { Card, Table, Button, Modal, Tree, message, Tag, Input, Space, Tooltip, Popconfirm } from 'antd'
import { EditOutlined, SafetyOutlined, SearchOutlined } from '@ant-design/icons'
import { permissionApi } from '../services/api'

interface StaffItem {
  user_id: number | null
  employee_id: number
  real_name: string
  phone: string
  department: string
  position: string
  roles: { id: number; name: string; code: string }[]
  permission_count: number
}

interface PermissionItem {
  id: number
  code: string
  name: string
  description: string
  resource: string
  action: string
  menu_path: string | null
}

const Permissions: React.FC = () => {
  const [staff, setStaff] = useState<StaffItem[]>([])
  const [permissions, setPermissions] = useState<PermissionItem[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState<StaffItem | null>(null)
  const [checkedKeys, setCheckedKeys] = useState<number[]>([])
  const [saving, setSaving] = useState(false)
  const [searchText, setSearchText] = useState('')

  useEffect(() => {
    loadStaff()
    loadPermissions()
  }, [])

  const loadStaff = async () => {
    setLoading(true)
    try {
      const res = await permissionApi.getStaffList()
      if (res.code === 200 || res.code === 0) {
        setStaff(res.data || [])
      }
    } catch (err) {
      message.error('加载员工列表失败')
    } finally {
      setLoading(false)
    }
  }

  const loadPermissions = async () => {
    try {
      const res = await permissionApi.getPermissions()
      if (res.code === 200 || res.code === 0) {
        setPermissions(res.data || [])
      }
    } catch (err) {
      message.error('加载权限列表失败')
    }
  }

  const handleAssign = async (record: StaffItem) => {
    setSelectedStaff(record)
    setModalVisible(true)
    
    // 加载员工当前权限
    try {
      const res = await permissionApi.getStaffPermissions(record.employee_id)
      if (res.code === 200 || res.code === 0) {
        setCheckedKeys(res.data.permission_ids || [])
      }
    } catch (err) {
      setCheckedKeys([])
    }
  }

  const handleSave = async () => {
    if (!selectedStaff) return
    
    setSaving(true)
    try {
      const res = await permissionApi.assignStaffPermissions({
        employee_id: selectedStaff.employee_id,
        user_id: selectedStaff.user_id || undefined,
        permission_ids: checkedKeys
      })
      
      if (res.code === 200 || res.code === 0) {
        message.success('权限分配成功')
        setModalVisible(false)
        loadStaff() // 刷新列表
      } else {
        message.error(res.message || '分配失败')
      }
    } catch (err: any) {
      message.error(err.response?.data?.message || '分配失败')
    } finally {
      setSaving(false)
    }
  }

  // 构建树形数据（按resource分组）
  const buildTreeData = () => {
    const groups: { [key: string]: PermissionItem[] } = {}
    
    permissions.forEach(p => {
      const resource = p.resource || '其他'
      if (!groups[resource]) groups[resource] = []
      groups[resource].push(p)
    })

    return Object.keys(groups).map(resource => ({
      title: resource,
      key: `resource_${resource}`,
      children: groups[resource].map(p => ({
        title: (
          <span>
            <SafetyOutlined style={{ marginRight: 8 }} />
            {p.name}
            <Tag style={{ marginLeft: 8 }}>{p.code}</Tag>
            {p.menu_path && <Tag color="blue">{p.menu_path}</Tag>}
          </span>
        ),
        key: `perm_${p.id}`,
        permissionId: p.id
      }))
    }))
  }

  const handleTreeCheck = (checked: any) => {
    // 只提取权限ID（过滤掉resource_开头的）
    const permIds = checked.filter((key: string) => key.startsWith('perm_'))
      .map((key: string) => parseInt(key.replace('perm_', '')))
    setCheckedKeys(permIds)
  }

  const columns = [
    {
      title: '员工',
      dataIndex: 'real_name',
      render: (text: string, record: StaffItem) => (
        <span>
          <strong>{text || '未设置姓名'}</strong>
          {record.phone && <div style={{ fontSize: 12, color: '#999' }}>{record.phone}</div>}
        </span>
      )
    },
    {
      title: '部门/职位',
      render: (record: StaffItem) => (
        <span>
          {record.department && <div>{record.department}</div>}
          {record.position && <div style={{ fontSize: 12, color: '#999' }}>{record.position}</div>}
        </span>
      )
    },
    {
      title: '角色',
      render: (record: StaffItem) => (
        <span>
          {record.roles && record.roles.length > 0 ? (
            record.roles.map((r, i) => <Tag key={i} color="blue">{r.name}</Tag>)
          ) : (
            <Tag>无角色</Tag>
          )}
        </span>
      )
    },
    {
      title: '直接权限数',
      dataIndex: 'permission_count',
      render: (count: number) => <Tag color={count > 0 ? 'green' : 'default'}>{count || 0} 个</Tag>
    },
    {
      title: '操作',
      render: (record: StaffItem) => (
        <Tooltip title="分配直接权限">
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => handleAssign(record)}
          >
            分配权限
          </Button>
        </Tooltip>
      )
    }
  ]

  const filteredStaff = staff.filter(s => 
    !searchText || 
    (s.real_name && s.real_name.includes(searchText)) ||
    (s.phone && s.phone.includes(searchText)) ||
    (s.department && s.department.includes(searchText))
  )

  return (
    <div>
      <Card 
        title="员工直接权限分配" 
        extra={
          <Space>
            <Input
              placeholder="搜索员工..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <Button onClick={() => { loadStaff(); loadPermissions() }}>刷新</Button>
          </Space>
        }
      >
        <Table
          rowKey="employee_id"
          columns={columns}
          dataSource={filteredStaff}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={`分配权限 - ${selectedStaff?.real_name || ''}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        confirmLoading={saving}
        width={700}
      >
        <div style={{ marginBottom: 16 }}>
          <Tag color="blue">直接权限</Tag>
          直接分配给员工的权限（优先级高于角色权限）
        </div>
        <Tree
          checkable
          defaultExpandAll
          checkedKeys={checkedKeys.map(id => `perm_${id}`)}
          onCheck={handleTreeCheck}
          treeData={buildTreeData()}
        />
      </Modal>
    </div>
  )
}

export default Permissions
